import React, {
    useCallback, useEffect, useMemo, useState, FC, ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { adaptFiles } from 'adaptors/adaptFiles';
import SelectElement from 'components/AtomicStructure/atoms/SelectElement';
import { MAX_FILE_SIZE } from 'constants/constants';
import SM from 'services/ServiceManager';
import { ICON_TYPE_CLOSE_SLIM } from 'ui-library/components/Icon';
import Error from '../../../../../components/AtomicStructure/atoms/Error';
import UploadDocuments from '../../../../../components/UploadDocuments/UploadDocuments';
import {
    Button, Icon, Loader, Modal, notification,
} from '../../../../../ui-library';
import { getBase64FromFile } from '../../../../../utils/file';
import { UPLOAD_DOCUMENT_CONFIG } from '../constants';

import './DocumentUpload.css';

// --- Types & Interfaces ---

interface ScreenConfig {
    id: number;
    width: number;
    primaryButtonText: string;
}

interface DocumentOption {
    type: string;
    translation: string;
    numberOfDocuments?: number;
    size?: number;
}

interface DocumentOptionsConfig {
    [key: string]: DocumentOption;
    personal: DocumentOption;
}

interface DocumentUploadProps {
    connectionClientId?: string | number;
    contactId: number;
    onUpload: (result: unknown) => Promise<void>;
    handleUploadNonIdDocument?: (
        files: File[],
        onSuccess: (result: unknown) => Promise<void>,
        type: string
    ) => void;
    documentOptions?: DocumentOptionsConfig;
}

const SCREEN: Record<'documentType' | 'upload', ScreenConfig> = {
    documentType: {
        id: 1,
        width: 400,
        primaryButtonText: 'clientDashboard.documents.next',
    },
    upload: {
        id: 2,
        width: 800,
        primaryButtonText: 'clientDashboard.documents.upload',
    },
};

export const DocumentUpload: FC<DocumentUploadProps> = ({
    connectionClientId,
    contactId,
    onUpload,
    handleUploadNonIdDocument,
    documentOptions = UPLOAD_DOCUMENT_CONFIG as DocumentOptionsConfig,
}) => {
    const { t } = useTranslation();

    // State
    const [showModal, setShowModal] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedDocumentType, setSelectedDocumentType] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [screen, setScreen] = useState<ScreenConfig>(SCREEN.documentType);
    const [files, setFiles] = useState<File[] | null>(null);
    const [numberOfDocumentsRequired, setNumberOfDocumentsRequired] = useState<number | undefined>(0);
    const [documentSize, setDocumentSize] = useState<number | null>(null);

    const hideModal = (): void => {
        setScreen(SCREEN.documentType);
        setShowModal(false);
        setError(null);
        setIsLoading(false);
        setSelectedDocumentType('');
        setFiles(null);
    };

    const documentTypes = useMemo(() => [
        { label: t('position.pleaseSelect'), id: '' },
        ...Object.keys(documentOptions).map(key => ({
            id: key,
            label: t(documentOptions[key].translation),
        })),
    ], [t, documentOptions]);

    const uploadPersonalDocs = useCallback(async () => {
        if (!files) return null;

        const payload = {
            ownerId: connectionClientId || contactId,
            documents: await Promise.all(files.map(async (file: File) => {
                const base64 = await getBase64FromFile(file);

                return {
                    filename: file.name,
                    file: base64.split(',')[1],
                };
            })),
        };

        const { data } = await SM.customDocumentsService('uploadIdDocument', [payload]);

        return data;
    }, [connectionClientId, contactId, files]);

    const uploadClientDataDocs = useCallback(async () => {
        if (!files) return null;
        const fileParams = await adaptFiles(contactId, files);
        const { data } = await SM.documents('postDocument', [fileParams]);

        return data;
    }, [contactId, files]);

    const onUploadSuccess = useCallback(async (result: unknown): Promise<void> => {
        notification.open({
            content: t('clientDashboard.documents.uploadSuccess'),
            type: 'success',
        });
        hideModal();
        if (result) {
            await onUpload(result);
        }
    }, [onUpload, t]);

    const uploadFile = useCallback(async (): Promise<void> => {
        try {
            setIsLoading(true);
            let result: unknown = null;

            if (selectedDocumentType === documentOptions.personal.type) {
                result = await uploadPersonalDocs();
            } else {
                if (handleUploadNonIdDocument && files) {
                    handleUploadNonIdDocument(files, onUploadSuccess, selectedDocumentType);

                    return;
                }
                result = await uploadClientDataDocs();
            }
            await onUploadSuccess(result);
        } catch (err: any) {
            if (err?.message) {
                setError(err.message);
            } else {
                setError(String(err));
            }
        } finally {
            setIsLoading(false);
        }
    }, [
        onUploadSuccess,
        uploadClientDataDocs,
        uploadPersonalDocs,
        selectedDocumentType,
        documentOptions,
        handleUploadNonIdDocument,
        files,
    ]);

    const onOk = useCallback(async (): Promise<void> => {
        if (screen.id === SCREEN.documentType.id && !selectedDocumentType) {
            setError(t('clientDashboard.documents.documentCategoryRequired'));
        } else if (screen.id === SCREEN.documentType.id) {
            setScreen(SCREEN.upload);
        } else if (!files?.length) {
            setError(t('clientDashboard.documents.noFileUploadError'));
        } else {
            await uploadFile();
        }
    }, [screen.id, uploadFile, selectedDocumentType, files, t]);

    const onBackClick = useCallback((): void => {
        setScreen(SCREEN.documentType);
        setError(null);
    }, []);

    const renderTitle = useMemo((): ReactNode => {
        if (screen.id === SCREEN.documentType.id) {
            return t('advisoryDashboard.documents.uploadDocument');
        }

        const currentOption = documentOptions[selectedDocumentType];

        return (
            <div className="modal-title">
                <div>
                    {`${t('advisoryDashboard.documents.uploadDocument')} - ${t(currentOption?.translation || '')}`}
                </div>
                {selectedDocumentType === documentOptions.personal.type && (
                    <div className="warning">{t('advisoryDashboard.documents.warning')}</div>
                )}
                <Button type="link" onClick={onBackClick}>
                    <Icon size={10} type="arrow-left" />
                    {t('clientDashboard.documents.back')}
                </Button>
            </div>
        );
    }, [screen.id, t, selectedDocumentType, documentOptions, onBackClick]);

    useEffect(() => {
        const option = documentOptions[selectedDocumentType];

        setNumberOfDocumentsRequired(option?.numberOfDocuments);
        setDocumentSize(option?.size ?? MAX_FILE_SIZE);
        setError(null);
    }, [selectedDocumentType, documentOptions]);

    const closeIcon = useMemo(() => (<Icon type={ICON_TYPE_CLOSE_SLIM} size={20} />), []);

    return (
        <>
            <Button onClick={() => setShowModal(true)}>
                {t('advisoryDashboard.documents.upload')}
            </Button>
            {showModal && (
                <Modal
                    title={renderTitle}
                    visible
                    okText={t(screen.primaryButtonText)}
                    cancelText={t('advisoryDashboard.documents.cancel')}
                    // Handle Loader vs Function type requirements for onOk
                    onOk={isLoading ? undefined : onOk}
                    okButtonProps={{ disabled: isLoading, loading: isLoading }}
                    cancelButtonProps={{ disabled: isLoading }}
                    onCancel={hideModal}
                    width={screen.width}
                    className="upload-document-modal"
                    borderRadius
                    maskClosable={false}
                    closeIcon={closeIcon}
                    closable
                >
                    {isLoading && (
                        <div className="modal-loader-overlay">
                            <Loader />
                        </div>
                    )}

                    {screen.id === SCREEN.documentType.id ? (
                        <SelectElement
                            options={documentTypes}
                            value={selectedDocumentType}
                            onChange={(val: string) => setSelectedDocumentType(val)}
                        />
                    ) : (
                        <UploadDocuments
                            onSuccess={setFiles}
                            onError={(msg: string) => setError(msg)}
                            numberOfFiles={numberOfDocumentsRequired}
                            maxMbFileSize={documentSize as number}
                        />
                    )}

                    {error && <Error>{error}</Error>}
                </Modal>
            )}
        </>
    );
};

export default DocumentUpload;
